import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { leadDistributionService, brandService } from '../../services/api';
import { INDIA_STATES_CITIES } from '../../data/indiaCities';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { Spinner } from '../../components/Spinner';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
  XMarkIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';

const REASON_LABELS = {
  missing_brand:      'No brand on lead',
  missing_city:       'No city on lead',
  unrecognized_brand: 'Brand not in catalog',
  no_matching_dealer: 'No dealer covers this brand + city',
  quota_exceeded:     'Lead pack full',
  invalid_phone:      'No valid phone number',
};

const PERIOD_LABELS = { day: 'per day', week: 'per week', month: 'per month' };
const WEEKDAYS      = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_DATES   = Array.from({ length: 31 }, (_, i) => i + 1);
const ordinal = (n) => { const v = n % 100; return n + (['th', 'st', 'nd', 'rd'][(v - 20) % 10] || ['th', 'st', 'nd', 'rd'][v] || 'th'); };
const shortDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

// "per week · resets Monday" / "per month · resets on the 5th"
const resetText = (q) => {
  if (q.period === 'week')  return `resets ${WEEKDAYS[q.weekStartDay ?? 1]}`;
  if (q.period === 'month') return `resets on the ${ordinal(q.monthStartDate ?? 1)}${(q.monthStartDate ?? 1) > 28 ? ' (month-end if shorter)' : ''}`;
  return 'resets daily';
};

const METHOD_LABELS = {
  auto_round_robin:  'Auto-distributed',
  page_override:     'Page route',
  manual_override:   'Manual assign',
  unassigned_review: 'Manual review',
};

const ALL_CITIES = Array.from(
  new Set(Object.values(INDIA_STATES_CITIES).flat())
).sort();

const titleCase = (s) => (s || '').replace(/\b\w/g, c => c.toUpperCase());

// Label for an account that has since been deleted — the log keeps only the
// id and role, the name is gone with the user document.
const deletedLabel = (role) => `Deleted ${(role || 'user').replace('_', ' ')}`;

// ── Chip picker: selected values as chips + typeahead over `suggestions` ───────

function TagPicker({ label, values, suggestions = [], onChange, placeholder }) {
  const [text, setText] = useState('');

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q || !suggestions.length) return [];
    return suggestions
      .filter(s => s.toLowerCase().includes(q) && !values.includes(s.toLowerCase()))
      .slice(0, 8);
  }, [text, suggestions, values]);

  const add = (raw) => {
    const v = (raw || '').trim().toLowerCase();
    if (!v || values.includes(v)) { setText(''); return; }
    onChange([...values, v]);
    setText('');
  };

  return (
    <div>
      <label className="label">{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {values.length === 0 && <span className="text-xs text-gray-400 italic">None set</span>}
        {values.map(v => (
          <span key={v} className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 text-xs font-medium px-2 py-1 rounded-md">
            {titleCase(v)}
            <button type="button" onClick={() => onChange(values.filter(x => x !== v))} className="hover:text-primary-900">
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="relative">
        <div className="flex gap-2">
          <input
            className="input"
            value={text}
            placeholder={placeholder}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(text); } }}
          />
          <button type="button" onClick={() => add(text)} className="btn-outline-secondary px-3 py-2 flex-shrink-0">
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>
        {matches.length > 0 && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {matches.map(m => (
              <button
                key={m}
                type="button"
                onClick={() => add(m)}
                className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              >
                {m}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Dealer coverage editor ────────────────────────────────────────────────────

function DealerEditor({ dealer, brands, onClose }) {
  const queryClient = useQueryClient();
  const [selBrands, setSelBrands]   = useState((dealer.brands || []).map(b => String(b._id)));
  const [cities, setCities]         = useState(dealer.territory?.cities || []);
  const [pincodes, setPincodes]     = useState(dealer.territory?.pincodes || []);
  const [enabled, setEnabled]       = useState(dealer.distributionEnabled !== false);

  const mutation = useMutation(
    (data) => leadDistributionService.updateDealerTerritory(dealer._id, data),
    {
      onSuccess: () => {
        toast.success('Coverage updated');
        queryClient.invalidateQueries('ldDealers');
        onClose();
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Failed to update coverage'),
    }
  );

  const toggleBrand = (id) => setSelBrands(prev =>
    prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]
  );

  return (
    <div className="bg-gray-50 border-t border-gray-200 px-6 py-5 space-y-5">
      <div>
        <label className="label">Brands</label>
        {brands.length === 0 ? (
          <p className="text-xs text-gray-500">No brands set up yet — add them under Settings → Brands &amp; Models.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {brands.map(b => (
              <button
                key={b._id}
                type="button"
                onClick={() => toggleBrand(String(b._id))}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  selBrands.includes(String(b._id))
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <TagPicker
          label="Cities"
          values={cities}
          suggestions={ALL_CITIES}
          onChange={setCities}
          placeholder="Type a city name…"
        />
        <TagPicker
          label="Pincodes (optional)"
          values={pincodes}
          onChange={(vals) => setPincodes(vals.map(v => v.replace(/\D/g, '')).filter(Boolean))}
          placeholder="Type a 6-digit pincode and press Enter…"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => setEnabled(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        Include this dealer in automatic lead distribution
      </label>

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onClose} className="btn-outline-secondary text-sm px-4 py-2">Cancel</button>
        <button
          onClick={() => mutation.mutate({ brands: selBrands, cities, pincodes, distributionEnabled: enabled })}
          disabled={mutation.isLoading}
          className="btn-primary text-sm px-4 py-2"
        >
          {mutation.isLoading ? <Spinner size="sm" /> : 'Save coverage'}
        </button>
      </div>
    </div>
  );
}

function DealerTerritory() {
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const { data, isLoading } = useQuery('ldDealers', leadDistributionService.getDealers);
  const { data: brandData }  = useQuery('brands', brandService.getBrands, { staleTime: 5 * 60 * 1000 });

  const allDealers = useMemo(() => data?.data?.data || [], [data]);
  const brands     = brandData?.data?.data?.brands || [];

  // Searching the brands and cities too, because "who covers Gurgaon?" and
  // "who sells Skoda?" are the questions this list is actually opened for.
  const dealers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allDealers;
    return allDealers.filter(d => [
      `${d.firstName} ${d.lastName}`,
      ...(d.brands || []).map(b => b.name),
      ...(d.territory?.cities || []),
    ].join(' ').toLowerCase().includes(term));
  }, [allDealers, search]);

  if (isLoading) return <div className="p-10 text-center"><Spinner size="md" /></div>;

  if (!allDealers.length) {
    return (
      <div className="p-10 text-center text-sm text-gray-500">
        No dealers in this workspace yet. Add a user with the Dealer role first.
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {allDealers.length >= 8 && (
        <div className="px-6 py-3 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search dealer, brand or city…"
              className="input text-sm w-full pl-9"
            />
          </div>
          <span className="text-xs text-gray-400">
            {dealers.length} of {allDealers.length}
          </span>
        </div>
      )}

      {!dealers.length && (
        <div className="px-6 py-10 text-center text-sm text-gray-500">
          No dealer matches &ldquo;{search}&rdquo;.
        </div>
      )}

      {dealers.map(d => {
        const cities = d.territory?.cities || [];
        const isOff  = d.distributionEnabled === false || d.status !== 'active';
        return (
          <div key={d._id}>
            <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900">{d.firstName} {d.lastName}</p>
                  {isOff && <span className="badge bg-gray-100 text-gray-600">Not in rotation</span>}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {(d.brands || []).length ? (d.brands || []).map(b => b.name).join(', ') : 'No brands'}
                  {' · '}
                  {cities.length ? `${cities.length} ${cities.length === 1 ? 'city' : 'cities'}` : 'No cities'}
                </p>
                {cities.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1 truncate max-w-xl">
                    {cities.map(titleCase).join(', ')}
                  </p>
                )}
              </div>
              <button
                onClick={() => setEditingId(editingId === d._id ? null : d._id)}
                className="btn-outline-secondary text-xs px-3 py-1.5"
              >
                {editingId === d._id ? 'Close' : 'Edit coverage'}
              </button>
            </div>
            {editingId === d._id && (
              <DealerEditor dealer={d} brands={brands} onClose={() => setEditingId(null)} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Manual review queue ───────────────────────────────────────────────────────

const PAGE_SIZES   = [25, 50, 100];
const NO_BRAND     = '__none__';   // matches the backend's "no brand" filter value
const EMPTY_FILTERS = { name: '', phone: '', brand: '', city: '', from: '', to: '' };

// A text filter applied once typing pauses, so the queue isn't re-fetched per keystroke
function useDebounced(value, ms = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

// Local calendar day → ISO bounds, so "8 Oct" means the viewer's 8 Oct
const dayStart = (d) => (d ? new Date(`${d}T00:00:00`).toISOString() : undefined);
const dayEnd   = (d) => (d ? new Date(`${d}T23:59:59.999`).toISOString() : undefined);

const dealerOptions = (dealers, fullPacks) => dealers.map(d => ({
  value: d._id,
  label: `${d.firstName} ${d.lastName}${fullPacks?.has(String(d._id)) ? ' — pack full' : ''}`,
}));

function ReviewRow({ lead, options, selected, onToggle }) {
  const queryClient = useQueryClient();
  const [assignTo, setAssignTo] = useState('');

  const mutation = useMutation(
    () => leadDistributionService.resolveReviewItem(lead._id, { assignedTo: assignTo }),
    {
      onSuccess: () => {
        toast.success('Lead assigned');
        queryClient.invalidateQueries('ldReviewQueue');
        queryClient.invalidateQueries('ldStats');
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Failed to assign lead'),
    }
  );

  return (
    <tr className={selected ? 'bg-primary-50' : 'hover:bg-gray-50'}>
      <td className="pl-4 pr-2 py-3 w-8">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(lead._id)}
          aria-label={`Select ${lead.customerInfo?.name || 'lead'}`}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
        />
      </td>
      <td className="px-4 py-3">
        <p className="text-sm font-medium text-gray-900">{lead.customerInfo?.name}</p>
        <p className="text-xs text-gray-500">{lead.customerInfo?.phone}</p>
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">{lead.leadInfo?.brand || <span className="text-gray-400">—</span>}</td>
      <td className="px-4 py-3 text-sm text-gray-700">
        {lead.customerInfo?.city || <span className="text-gray-400">—</span>}
        {lead.customerInfo?.pincode && <span className="text-xs text-gray-400 block">{lead.customerInfo.pincode}</span>}
      </td>
      <td className="px-4 py-3">
        <span className="badge bg-amber-100 text-amber-700">
          {REASON_LABELS[lead.distribution?.reason] || 'Needs review'}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
        {new Date(lead.createdAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 justify-end">
          <SearchableSelect
            value={assignTo}
            onChange={setAssignTo}
            options={options}
            allLabel="Assign to…"
            placeholder="Search dealer…"
            buttonClassName="input py-1.5 text-xs w-44"
          />
          <button
            onClick={() => mutation.mutate()}
            disabled={!assignTo || mutation.isLoading}
            className="btn-primary text-xs px-3 py-1.5 disabled:opacity-40"
          >
            {mutation.isLoading ? <Spinner size="sm" /> : 'Assign'}
          </button>
        </div>
      </td>
    </tr>
  );
}

function ReviewFilters({ filters, setFilter, brands, hasNoBrand, onClear, active }) {
  return (
    <div className="px-4 py-3 border-b border-gray-100 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 items-end">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Customer name</label>
        <input className="input text-sm" value={filters.name} onChange={e => setFilter('name', e.target.value)} placeholder="Search name…" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Mobile number</label>
        <input className="input text-sm" inputMode="numeric" value={filters.phone} onChange={e => setFilter('phone', e.target.value)} placeholder="Search mobile…" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Brand</label>
        <select className="input text-sm" value={filters.brand} onChange={e => setFilter('brand', e.target.value)}>
          <option value="">All brands</option>
          {brands.map(b => <option key={b} value={b}>{b}</option>)}
          {hasNoBrand && <option value={NO_BRAND}>No brand</option>}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
        <input className="input text-sm" value={filters.city} onChange={e => setFilter('city', e.target.value)} placeholder="Search city…" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Received from</label>
        <input type="date" className="input text-sm" value={filters.from} max={filters.to || undefined} onChange={e => setFilter('from', e.target.value)} />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Received to</label>
        <div className="flex gap-2">
          <input type="date" className="input text-sm min-w-0" value={filters.to} min={filters.from || undefined} onChange={e => setFilter('to', e.target.value)} />
          {active && (
            <button onClick={onClear} title="Clear filters" className="btn-outline-secondary px-2 flex-shrink-0">
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewQueue() {
  const queryClient = useQueryClient();
  const [filters, setFilters]   = useState(EMPTY_FILTERS);
  const [page, setPage]         = useState(1);
  const [limit, setLimit]       = useState(25);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkTarget, setBulkTarget] = useState('');

  const name  = useDebounced(filters.name);
  const phone = useDebounced(filters.phone);
  const city  = useDebounced(filters.city);

  const params = {
    page, limit,
    name:  name.trim()  || undefined,
    phone: phone.replace(/\D/g, '') || undefined,
    city:  city.trim()  || undefined,
    brand: filters.brand || undefined,
    from:  dayStart(filters.from),
    to:    dayEnd(filters.to),
  };

  const { data, isLoading, isFetching } = useQuery(
    ['ldReviewQueue', params],
    () => leadDistributionService.getReviewQueue(params),
    { keepPreviousData: true }
  );
  const { data: dealerData } = useQuery('ldDealers', leadDistributionService.getDealers);
  const { data: quotaData }  = useQuery('ldQuotas', leadDistributionService.getQuotas);

  const body   = data?.data?.data || {};
  const leads  = body.leads || [];
  const total  = body.total || 0;
  const pages  = Math.max(Math.ceil(total / limit), 1);
  const facets = body.facets || { brands: [], hasNoBrand: false };

  const dealers = (dealerData?.data?.data || []).filter(d => d.status === 'active');
  // Dealers whose own or team pack is used up — still assignable (it's the
  // admin's call), but worth flagging in the dropdown.
  const fullPacks = new Set((quotaData?.data?.data || []).filter(r => r.exhausted).map(r => String(r._id)));
  const options   = dealerOptions(dealers, fullPacks);

  const filtersActive = Object.values(filters).some(Boolean);

  // A changed filter is a new list: back to page 1, and a selection made
  // against the old list no longer applies.
  const filterKey = JSON.stringify([name, phone, city, filters.brand, filters.from, filters.to]);
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [filterKey]);

  // Assigning the last rows of the last page leaves it empty — step back.
  useEffect(() => {
    if (!isFetching && page > 1 && page > pages) setPage(pages);
  }, [isFetching, page, pages]);

  const setFilter = (key, value) => setFilters(f => ({ ...f, [key]: value }));

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const pageIds        = leads.map(l => l._id);
  const pageSelected   = pageIds.filter(id => selected.has(id)).length;
  const allPageChecked = pageIds.length > 0 && pageSelected === pageIds.length;
  const togglePage = () => setSelected(prev => {
    const next = new Set(prev);
    if (allPageChecked) pageIds.forEach(id => next.delete(id));
    else pageIds.forEach(id => next.add(id));
    return next;
  });

  const bulkMutation = useMutation(
    () => leadDistributionService.resolveReviewItems({ leadIds: [...selected], assignedTo: bulkTarget }),
    {
      onSuccess: (res) => {
        toast.success(res.data?.message || 'Leads assigned');
        setSelected(new Set());
        setBulkTarget('');
        queryClient.invalidateQueries('ldReviewQueue');
        queryClient.invalidateQueries('ldStats');
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Failed to assign leads'),
    }
  );

  if (isLoading) return <div className="p-10 text-center"><Spinner size="md" /></div>;

  if (!total && !filtersActive) {
    return (
      <div className="p-10 text-center">
        <p className="text-sm font-medium text-gray-700">Nothing needs review</p>
        <p className="text-xs text-gray-500 mt-1">Every incoming lead has matched a dealer.</p>
      </div>
    );
  }

  const first = total ? (page - 1) * limit + 1 : 0;
  const last  = Math.min(page * limit, total);

  return (
    <>
      <ReviewFilters
        filters={filters}
        setFilter={setFilter}
        brands={facets.brands}
        hasNoBrand={facets.hasNoBrand}
        active={filtersActive}
        onClear={() => setFilters(EMPTY_FILTERS)}
      />

      {selected.size > 0 && (
        <div className="px-4 py-3 bg-primary-50 border-b border-primary-100 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-primary-800">
            {selected.size} selected
          </span>
          <button onClick={() => setSelected(new Set())} className="text-xs text-primary-700 hover:underline">
            Clear selection
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <SearchableSelect
              value={bulkTarget}
              onChange={setBulkTarget}
              options={options}
              allLabel="Assign to…"
              placeholder="Search dealer…"
              buttonClassName="input py-1.5 text-xs w-52 bg-white"
            />
            <button
              onClick={() => bulkMutation.mutate()}
              disabled={!bulkTarget || bulkMutation.isLoading}
              className="btn-primary text-xs px-3 py-1.5 disabled:opacity-40"
            >
              {bulkMutation.isLoading ? <Spinner size="sm" /> : `Assign ${selected.size}`}
            </button>
          </div>
        </div>
      )}

      {!leads.length ? (
        <div className="p-10 text-center">
          <p className="text-sm font-medium text-gray-700">No leads match these filters</p>
          <button onClick={() => setFilters(EMPTY_FILTERS)} className="text-xs text-primary-600 hover:underline mt-1">
            Clear filters
          </button>
        </div>
      ) : (
        <div className={`overflow-x-auto ${isFetching ? 'opacity-60 transition-opacity' : ''}`}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="pl-4 pr-2 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allPageChecked}
                    ref={el => { if (el) el.indeterminate = pageSelected > 0 && !allPageChecked; }}
                    onChange={togglePage}
                    aria-label="Select all on this page"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                  />
                </th>
                {['Customer', 'Brand', 'City', 'Reason', 'Received', ''].map((h, i) => (
                  <th key={i} className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider ${i === 5 ? 'text-right' : 'text-left'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {leads.map(l => (
                <ReviewRow key={l._id} lead={l} options={options} selected={selected.has(l._id)} onToggle={toggle} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 0 && (
        <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>Showing {first}–{last} of {total}</span>
            <label className="flex items-center gap-1.5">
              Rows
              <select
                value={limit}
                onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                className="border border-gray-300 rounded-md py-1 pl-2 pr-7 text-xs"
              >
                {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="btn-outline-secondary text-xs px-3 py-1.5 disabled:opacity-40">Previous</button>
            <span className="text-xs text-gray-500">Page {page} of {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
              className="btn-outline-secondary text-xs px-3 py-1.5 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Lead packs (quotas) ───────────────────────────────────────────────────────

function PackRow({ row, canEdit }) {
  const queryClient = useQueryClient();
  const savedLimit  = row.leadQuota?.limit ?? '';
  const savedPeriod = row.leadQuota?.period || 'day';
  const savedWeekDay   = row.leadQuota?.weekStartDay ?? 1;
  const savedMonthDate = row.leadQuota?.monthStartDate ?? 1;
  const [limit, setLimit]         = useState(savedLimit);
  const [period, setPeriod]       = useState(savedPeriod);
  const [weekDay, setWeekDay]     = useState(savedWeekDay);
  const [monthDate, setMonthDate] = useState(savedMonthDate);
  const savedTotal = row.leadQuota?.totalLimit ?? '';
  const [totalLimit, setTotalLimit] = useState(savedTotal);
  const dirty = String(limit) !== String(savedLimit) || period !== savedPeriod
    || String(totalLimit) !== String(savedTotal)
    || (period === 'week'  && Number(weekDay)   !== savedWeekDay)
    || (period === 'month' && Number(monthDate) !== savedMonthDate);

  const mutation = useMutation(
    () => leadDistributionService.updateQuota(row._id, {
      limit:          limit === '' ? null : Number(limit),
      totalLimit:     totalLimit === '' ? null : Number(totalLimit),
      period,
      weekStartDay:   Number(weekDay),
      monthStartDate: Number(monthDate),
    }),
    {
      onSuccess: () => {
        toast.success('Lead pack updated');
        queryClient.invalidateQueries('ldQuotas');
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Failed to update lead pack'),
    }
  );

  const hasLimit = row.leadQuota?.limit != null;
  const pct      = hasLimit ? Math.min((row.used / row.leadQuota.limit) * 100, 100) : 0;
  const hasTotal = row.leadQuota?.totalLimit != null;
  const totalPct = hasTotal ? Math.min((row.totalUsed / row.leadQuota.totalLimit) * 100, 100) : 0;
  const inactive = row.status !== 'active';
  // Leads this account can still get right now: the tighter of its two limits.
  const left = Math.min(row.remaining ?? Infinity, row.totalRemaining ?? Infinity);

  return (
    <tr className={`hover:bg-gray-50 ${inactive ? 'opacity-60' : ''}`}>
      <td className="px-4 py-3">
        <p className="text-sm font-medium text-gray-900">{row.firstName} {row.lastName}</p>
        <p className="text-xs text-gray-500">
          {row.userCode && <span className="font-mono">{row.userCode} · </span>}
          {row.role === 'admin' ? 'Admin · team pack' : 'Dealer'}
          {inactive && ' · inactive'}
        </p>
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">
        {row.parent?.name || <span className="text-gray-400">—</span>}
      </td>
      <td className="px-4 py-3">
        {canEdit ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              step="1"
              value={totalLimit}
              onChange={e => setTotalLimit(e.target.value)}
              placeholder="Pack size"
              title="Total leads in the pack (e.g. 100). Changing it starts a new pack."
              className="input py-1.5 text-sm w-24"
            />
            <span className="text-xs text-gray-400">leads,</span>
            <input
              type="number"
              min="1"
              step="1"
              value={limit}
              onChange={e => setLimit(e.target.value)}
              placeholder="Unlimited"
              className="input py-1.5 text-sm w-28"
            />
            <select value={period} onChange={e => setPeriod(e.target.value)} className="input py-1.5 text-sm w-32">
              {Object.entries(PERIOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {period === 'week' && (
              <select value={weekDay} onChange={e => setWeekDay(e.target.value)} className="input py-1.5 text-sm w-40" title="Day the weekly pack resets">
                {WEEKDAYS.map((d, i) => <option key={i} value={i}>resets {d}</option>)}
              </select>
            )}
            {period === 'month' && (
              <select value={monthDate} onChange={e => setMonthDate(e.target.value)} className="input py-1.5 text-sm w-40" title="Date the monthly pack resets (31 = month-end)">
                {MONTH_DATES.map(n => <option key={n} value={n}>resets on the {ordinal(n)}</option>)}
              </select>
            )}
          </div>
        ) : (
          <span className="text-sm text-gray-700">
            {hasTotal && <>{row.leadQuota.totalLimit} lead pack{hasLimit ? ', ' : ''}</>}
            {hasLimit
              ? <>{row.leadQuota.limit} {PERIOD_LABELS[savedPeriod]} <span className="text-gray-400">· {resetText(row.leadQuota)}</span></>
              : !hasTotal && 'Unlimited'}
          </span>
        )}
      </td>
      <td className="px-4 py-3 min-w-[12rem]">
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full rounded-full ${hasLimit && row.used >= row.leadQuota.limit ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-primary-500'}`}
              style={{ width: `${hasLimit ? pct : 0}%` }}
            />
          </div>
          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
            {row.used}{hasLimit ? ` / ${row.leadQuota.limit}` : ''}
          </span>
        </div>
        <p className="text-xs text-gray-400 mt-0.5">
          {savedPeriod === 'day'
            ? 'today'
            : `since ${shortDate(row.periodStart)} · resets ${shortDate(row.resetsAt)}`}
        </p>
        {hasTotal && (
          <div className="mt-1.5">
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full rounded-full ${row.totalUsed >= row.leadQuota.totalLimit ? 'bg-red-500' : totalPct >= 70 ? 'bg-amber-500' : 'bg-primary-500'}`}
                  style={{ width: `${totalPct}%` }}
                />
              </div>
              <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">{row.totalUsed} / {row.leadQuota.totalLimit}</span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">whole pack</p>
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        {row.exhausted
          ? <span className="badge bg-red-100 text-red-700">{row.exhaustedBy === 'total' ? 'Pack finished' : 'Limit full'}</span>
          : row.nearLimit
            ? <span className="badge bg-amber-100 text-amber-700" title="70% or more of a limit is used">⚑ {left} left</span>
            : hasLimit || hasTotal
              ? <span className="badge bg-green-100 text-green-700">{left} left</span>
              : <span className="badge bg-gray-100 text-gray-600">No limit</span>}
      </td>
      {canEdit && (
        <td className="px-4 py-3 text-right">
          <button
            onClick={() => mutation.mutate()}
            disabled={!dirty || mutation.isLoading}
            className="btn-primary text-xs px-3 py-1.5 disabled:opacity-40"
          >
            {mutation.isLoading ? <Spinner size="sm" /> : 'Save'}
          </button>
        </td>
      )}
    </tr>
  );
}

function LeadPacks() {
  const { user } = useAuth();
  const canEdit  = user?.role === 'super_admin' || user?.role === 'company';
  const { data, isLoading } = useQuery('ldQuotas', leadDistributionService.getQuotas);

  const rows    = data?.data?.data || [];
  const admins  = rows.filter(r => r.role === 'admin');
  const dealers = rows.filter(r => r.role === 'dealer');

  if (isLoading) return <div className="p-10 text-center"><Spinner size="md" /></div>;

  if (!rows.length) {
    return (
      <div className="p-10 text-center text-sm text-gray-500">
        No admins or dealers in this workspace yet.
      </div>
    );
  }

  const headers = ['Account', 'Reports to', 'Lead pack', 'Used', 'Status', ...(canEdit ? [''] : [])];
  const groupRow = (label, hint) => (
    <tr className="bg-gray-50">
      <td colSpan={headers.length} className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
        {label} <span className="font-normal normal-case text-gray-400">· {hint}</span>
      </td>
    </tr>
  );

  return (
    <div>
      <div className="px-6 py-4 border-b border-gray-100 text-sm text-gray-600">
        Set a pack size (e.g. 100 leads) and, if you like, a pace per day, week or month (e.g. 10 per day). Leads stop
        when either runs out, and the row is flagged amber once 70% of a limit is used. A pack caps how many leads
        auto-distribution hands an account. Once a dealer's
        pack — or their admin's team pack — is full, matching leads skip them; if nobody else matches, the lead
        stays with the account it arrived at, goes to Manual Review, and the admin and super admin are emailed.
        Daily packs reset at midnight; weekly and monthly packs reset on the day or date you pick.
        {!canEdit && <span className="block mt-1 text-xs text-gray-400">Only the super admin can change packs.</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((h, i) => (
                <th key={i} className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider ${i === headers.length - 1 && canEdit ? 'text-right' : 'text-left'}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {admins.length > 0 && groupRow('Admins', 'team pack counts leads to everyone under the admin')}
            {admins.map(r => <PackRow key={`${r._id}-${r.leadQuota?.limit}-${r.leadQuota?.period}-${r.leadQuota?.totalLimit}`} row={r} canEdit={canEdit} />)}
            {dealers.length > 0 && groupRow('Dealers', 'own pack')}
            {dealers.map(r => <PackRow key={`${r._id}-${r.leadQuota?.limit}-${r.leadQuota?.period}-${r.leadQuota?.totalLimit}`} row={r} canEdit={canEdit} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Activity log ──────────────────────────────────────────────────────────────

function ActivityLog() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery(
    ['ldLogs', page],
    () => leadDistributionService.getLogs({ page, limit: 25 }),
    { keepPreviousData: true }
  );

  const logs  = data?.data?.data?.logs  || [];
  const total = data?.data?.data?.total || 0;
  const pages = Math.ceil(total / 25) || 1;

  if (isLoading) return <div className="p-10 text-center"><Spinner size="md" /></div>;

  if (!logs.length) {
    return <div className="p-10 text-center text-sm text-gray-500">No distribution activity yet.</div>;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['When', 'Lead', 'Brand / City', 'Assigned to', 'How'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {logs.map(log => (
              <tr key={log._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                  {new Date(log.createdAt).toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  <p className="text-sm text-gray-900">{log.leadId?.customerInfo?.name || 'Lead removed'}</p>
                  <p className="text-xs text-gray-500">{log.leadId?.customerInfo?.phone}</p>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {log.brand || '—'}
                  <span className="text-xs text-gray-400 block">{log.city || '—'}{log.pincode ? ` · ${log.pincode}` : ''}</span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {log.assignedTo
                    ? `${log.assignedTo.firstName} ${log.assignedTo.lastName}`
                    : <span className="text-gray-400 italic">{log.assignedRole ? deletedLabel(log.assignedRole) : 'Unassigned'}</span>}
                  {log.performedBy && (
                    <span className="text-xs text-gray-400 block">by {log.performedBy.firstName} {log.performedBy.lastName}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`badge ${log.method === 'unassigned_review' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {METHOD_LABELS[log.method] || log.method}
                  </span>
                  {log.reason && <span className="text-xs text-gray-400 block mt-1">{REASON_LABELS[log.reason]}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
          <p className="text-xs text-gray-500">Page {page} of {pages} · {total} entries</p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="btn-outline-secondary text-xs px-3 py-1.5 disabled:opacity-40">Previous</button>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
              className="btn-outline-secondary text-xs px-3 py-1.5 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'territory', label: 'Dealer Coverage', icon: MapPinIcon },
  { key: 'review',    label: 'Manual Review',   icon: ExclamationTriangleIcon },
  { key: 'packs',     label: 'Lead Packs',      icon: TicketIcon },
  { key: 'logs',      label: 'Activity Log',    icon: ClipboardDocumentListIcon },
];

export function LeadDistribution() {
  const [tab, setTab] = useState('territory');
  const { data: statsData } = useQuery('ldStats', leadDistributionService.getStats);
  const stats = statsData?.data?.data || {};

  const cards = [
    { label: 'Distributed today',   value: stats.distributedToday ?? '—' },
    { label: 'Last 7 days',         value: stats.distributedWeek ?? '—' },
    { label: 'Auto-distributed',    value: stats.byMethod?.auto_round_robin ?? 0, hint: 'last 7 days' },
    { label: 'Awaiting review',     value: stats.reviewCount ?? 0, warn: (stats.reviewCount ?? 0) > 0 },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Lead Distribution</h1>
        <p className="text-sm text-gray-500 mt-1">
          Facebook leads are routed automatically to the dealer covering the lead’s brand and city, rotating evenly when several match. Lead packs cap how many each account receives per period.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => (
          <div key={c.label} className="card px-5 py-4">
            <p className="text-xs font-medium text-gray-500">{c.label}</p>
            <p className={`text-2xl font-bold mt-1 ${c.warn ? 'text-amber-600' : 'text-gray-900'}`}>{c.value}</p>
            {c.hint && <p className="text-xs text-gray-400 mt-0.5">{c.hint}</p>}
          </div>
        ))}
      </div>

      {stats.perDealer?.length > 0 && (
        <div className="card">
          <div className="card-header flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-gray-400" />
            <h2 className="text-base font-semibold text-gray-900">Leads per dealer <span className="font-normal text-gray-400 text-sm">· last 7 days</span></h2>
          </div>
          <div className="card-body space-y-2.5">
            {stats.perDealer.map(d => {
              const max = Math.max(...stats.perDealer.map(x => x.count), 1);
              return (
                <div key={d._id} className="flex items-center gap-3">
                  <span className={`text-sm w-44 truncate ${d.deleted ? 'text-gray-400 italic' : 'text-gray-700'}`}>
                    {d.deleted ? deletedLabel(d.assignedRole) : `${d.firstName} ${d.lastName}`}
                  </span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <div className="bg-primary-500 h-full rounded-full" style={{ width: `${(d.count / max) * 100}%` }} />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-10 text-right">{d.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card">
        <div className="border-b border-gray-100 flex">
          {TABS.map(t => {
            const Icon = t.icon;
            const showBadge = t.key === 'review' && (stats.reviewCount ?? 0) > 0;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                {showBadge && (
                  <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                    {stats.reviewCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {tab === 'territory' && <DealerTerritory />}
        {tab === 'review'    && <ReviewQueue />}
        {tab === 'packs'     && <LeadPacks />}
        {tab === 'logs'      && <ActivityLog />}
      </div>
    </div>
  );
}
